import { Component } from '@theme/component';

/**
 * File upload field controller with drag-and-drop support.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} dropzone
 * @property {HTMLElement} fileItems
 *
 * @extends {Component<Refs>}
 */
class FileUploadComponent extends Component {
  /**
   * Required refs for the component to operate.
   *
   * @type {string[]}
   */
  requiredRefs = ['dropzone', 'fileItems'];

  /**
   * @type {(() => void) | null}
   */
  #formResetHandler = null;

  /**
   * @type {HTMLTemplateElement | null}
   */
  #itemTemplate = null;

  /**
   * Snapshot of selected files right before the native picker opens.
   * Used to append (not replace) in multi mode, since the browser
   * overwrites `input.files` with the new selection on change.
   *
   * @type {File[] | null}
   */
  #filesBeforePicker = null;

  connectedCallback() {
    super.connectedCallback();

    const template = this.querySelector('[data-file-item-template]');
    this.#itemTemplate = template instanceof HTMLTemplateElement ? template : null;

    const form = this.closest('form');
    if (form) {
      this.#formResetHandler = () => {
        requestAnimationFrame(() => this.#renderFiles());
      };
      form.addEventListener('reset', this.#formResetHandler);
    }

    this.#renderFiles();
  }

  disconnectedCallback() {
    const form = this.closest('form');
    if (form && this.#formResetHandler) {
      form.removeEventListener('reset', this.#formResetHandler);
    }
    super.disconnectedCallback();
  }

  /**
   * Opens the native file picker.
   *
   * @param {Event} event
   * @returns {void}
   */
  openPicker = (event) => {
    event.preventDefault();
    this.#stashCurrentFiles();
    this.#fileInput?.click();
  };

  /**
   * Opens picker for keyboard activation on dropzone.
   *
   * @param {KeyboardEvent} event
   * @returns {void}
   */
  handleDropzoneKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.#stashCurrentFiles();
    this.#fileInput?.click();
  };

  /**
   * Remembers the current selection so handleInputChange can merge new
   * picker results with existing files instead of overwriting.
   *
   * @returns {void}
   */
  #stashCurrentFiles() {
    if (!this.#fileInput?.multiple) return;
    this.#filesBeforePicker = [...(this.#fileInput.files ?? [])];
  }

  /**
   * Marks dragover state when a file enters dropzone.
   *
   * @param {DragEvent} event
   * @returns {void}
   */
  handleDragEnter = (event) => {
    event.preventDefault();
    this.classList.add('_dragover');
  };

  /**
   * Keeps dropzone in dragover state while dragging.
   *
   * @param {DragEvent} event
   * @returns {void}
   */
  handleDragOver = (event) => {
    event.preventDefault();
    this.classList.add('_dragover');
  };

  /**
   * Clears dragover state when leaving dropzone.
   *
   * @param {DragEvent} event
   * @returns {void}
   */
  handleDragLeave = (event) => {
    event.preventDefault();
    if (event.currentTarget.contains(event.relatedTarget)) return;
    this.classList.remove('_dragover');
  };

  /**
   * Accepts dropped files. In `multiple` mode appends to existing selection,
   * otherwise replaces with the first dropped file.
   *
   * @param {DragEvent} event
   * @returns {void}
   */
  handleDrop = (event) => {
    event.preventDefault();
    this.classList.remove('_dragover');
    this.#filesBeforePicker = null;

    const dropped = [...(event.dataTransfer?.files ?? [])];
    if (!dropped.length || !this.#fileInput) return;

    const merged = this.#fileInput.multiple
      ? this.#mergeFiles([...(this.#fileInput.files ?? [])], dropped)
      : [dropped[0]];

    this.#setInputFiles(merged);
    this.#fileInput.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /**
   * Clears field-level error state and re-renders file list after input change.
   *
   * @returns {void}
   */
  handleInputChange = () => {
    this.#clearFieldErrors();

    if (this.#filesBeforePicker !== null && this.#fileInput?.multiple) {
      const picked = [...(this.#fileInput.files ?? [])];
      const merged = this.#mergeFiles(this.#filesBeforePicker, picked);
      this.#filesBeforePicker = null;
      this.#setInputFiles(merged);
    } else {
      this.#filesBeforePicker = null;
    }

    this.#renderFiles();
  };

  /**
   * Public API for the owning form: highlights specific file items as
   * invalid. Pass an empty array (or no indices) to clear all highlights.
   * Indices refer to the current order of `input.files`.
   *
   * @param {Array<number | string>} [indices]
   * @returns {void}
   */
  setItemErrors = (indices = []) => {
    const errorSet = new Set((indices ?? []).map(Number).filter(Number.isFinite));
    [...this.refs.fileItems.children].forEach((item, idx) => {
      if (item instanceof HTMLElement) {
        item.classList.toggle('_error', errorSet.has(idx));
      }
    });
  };

  /**
   * Removes a specific file by index from the input's FileList.
   *
   * @param {{ index?: string | number }} data
   * @param {Event} event
   * @returns {void}
   */
  removeFile = (data, event) => {
    event.preventDefault();
    event.stopPropagation();
    const index = Number(data?.index);
    if (!Number.isFinite(index) || !this.#fileInput) return;

    // This change does not come from native picker selection.
    this.#filesBeforePicker = null;

    const files = [...(this.#fileInput.files ?? [])];
    if (index < 0 || index >= files.length) return;
    files.splice(index, 1);

    this.#setInputFiles(files);
    this.#fileInput.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /**
   * Clears error UI on the surrounding `.field` wrapper so stale backend
   * errors disappear as soon as the user changes the selection.
   *
   * @returns {void}
   */
  #clearFieldErrors() {
    this.classList.remove('_error');
    this.#fileInput?.removeAttribute('aria-invalid');

    const field = this.closest('.field');
    field?.classList.remove('_error');

    const errorEl = field?.querySelector('[data-field-error]');
    if (errorEl instanceof HTMLElement) {
      errorEl.textContent = '';
      errorEl.classList.add('_hidden');
    }
  }

  /**
   * Renders the list of currently selected files by cloning the item template.
   *
   * @returns {void}
   */
  #renderFiles() {
    const files = [...(this.#fileInput?.files ?? [])];
    const list = this.refs.fileItems;
    list.replaceChildren();

    const multiple = this.#fileInput?.multiple ?? false;
    const showDropzone = multiple || !files.length;
    this.refs.dropzone.classList.toggle('_hidden', !showDropzone);
    list.classList.toggle('_hidden', !files.length);

    if (!files.length || !this.#itemTemplate) return;

    files.forEach((file, index) => {
      const clone = this.#itemTemplate.content.firstElementChild?.cloneNode(true);
      if (!(clone instanceof HTMLElement)) return;

      const nameEl = clone.querySelector('[data-file-name]');
      if (nameEl instanceof HTMLElement) nameEl.textContent = file.name;

      const sizeEl = clone.querySelector('[data-file-size]');
      if (sizeEl instanceof HTMLElement) sizeEl.textContent = this.#formatFileSize(file.size);

      const removeBtn = clone.querySelector('[data-remove]');
      if (removeBtn instanceof HTMLElement) {
        removeBtn.setAttribute('on:click', `file-upload-component/removeFile?index=${index}`);
      }

      list.appendChild(clone);
    });
  }

  /**
   * Merges two file collections, deduplicating by name + size + lastModified.
   * Keeps the order of existing files first, then newcomers.
   *
   * @param {File[]} existing
   * @param {File[]} incoming
   * @returns {File[]}
   */
  #mergeFiles(existing, incoming) {
    const key = (/** @type {File} */ f) => `${f.name}|${f.size}|${f.lastModified}`;
    const seen = new Set(existing.map(key));
    const merged = [...existing];
    for (const file of incoming) {
      if (seen.has(key(file))) continue;
      seen.add(key(file));
      merged.push(file);
    }
    return merged;
  }

  /**
   * Writes a File[] back into the underlying input via DataTransfer.
   *
   * @param {File[]} files
   * @returns {void}
   */
  #setInputFiles(files) {
    if (!this.#fileInput) return;
    const transfer = new DataTransfer();
    for (const file of files) {
      transfer.items.add(file);
    }
    this.#fileInput.files = transfer.files;
  }

  /**
   * Converts bytes into a compact human-readable string.
   *
   * @param {number | undefined} bytes
   * @returns {string}
   */
  #formatFileSize(bytes) {
    if (!bytes || bytes < 1024) return `${bytes || 0} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  /**
   * Returns the underlying file input if present.
   *
   * @returns {HTMLInputElement | null}
   */
  get #fileInput() {
    const input = this.querySelector('input[type="file"]');
    return input instanceof HTMLInputElement ? input : null;
  }
}

if (!customElements.get('file-upload-component')) {
  customElements.define('file-upload-component', FileUploadComponent);
}
